import { DisplayerType } from '@highimpact-frontend/displayer-types';
import { GenericErrorBoundary } from '@highimpact/dashboard-ui';
import { PermissionDecorator } from '@highimpact/react-decorators';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { IconButton, Tooltip } from '@mui/material';
import { forwardRef, useMemo, type LegacyRef } from 'react';
import { useDashboardPermissionScope } from '../../../../hooks/useDashboardPermissionScope';
import { useDashboardLibraryDispatch } from '../../../../runtime/DashboardLibraryProvider';
import {
  setDeleteWidgetConfirmation,
  setWidgetConfigPanelState,
} from '../../../../state/dashboard/slice';
import NameHandlerComponent from '../../../NameHandlerComponent';
import { WidgetDragHandle } from '../../commons/WidgetDragHandle';
import { WidgetToolbar } from '../../commons/WidgetToolbar';
import { PlotProps } from '../../commons/plots/helpers';

interface DisplayerNavProps extends Omit<PlotProps, 'dataset' | 'displayer'> {
  gridType: 'grids' | 'reports';
  gridId: number;
  displayer: DisplayerType;
  buttons: {
    download?: boolean;
    zoom?: boolean;
    edit?: boolean;
    fullscreen?: boolean;
    delete?: boolean;
  };
}

function DisplayerNavComponent(
  {
    gridId,
    gridType,
    dashboardElementId,
    dashboardOnlyMode,
    widget_id,
    is_editable,
    buttons,
    displayer,
  }: DisplayerNavProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const dispatch = useDashboardLibraryDispatch();
  const { site_id, project_id } = useDashboardPermissionScope();
  const permissionExtra = useMemo(() => ({ dashboard_id: gridId }), [gridId]);

  const platformActions = useMemo(
    () => ({
      edit:
        is_editable && buttons.edit
          ? {
              visible: true as const,
              tooltipText: 'Panneau de configuration',
              permissionItem: 'dashboard_configure_widget' as const,
              onClick: () =>
                dispatch(
                  setWidgetConfigPanelState({
                    state: 'Reconfiguring',
                    elementType: 'plot',
                    gridId: gridId,
                    gridType: gridType,
                    widgetId: dashboardElementId,
                  })
                ),
            }
          : undefined,
      delete:
        is_editable && buttons.delete
          ? {
              visible: true as const,
              tooltipText: "Supprimer l'élément",
              permissionItem: 'dashboard_delete_widget' as const,
              onClick: () =>
                dispatch(
                  setDeleteWidgetConfirmation({
                    state: 'open',
                    dashboardElementId: dashboardElementId,
                    widgetName: displayer.name,
                  })
                ),
            }
          : undefined,
    }),
    [
      buttons.delete,
      buttons.edit,
      dashboardElementId,
      dispatch,
      displayer.name,
      gridId,
      gridType,
      is_editable,
    ]
  );

  return (
    <PermissionDecorator
      item="dashboard_nav_bar_widget"
      project_id={project_id}
      site_id={site_id}
      extra={permissionExtra}
    >
      <div ref={ref} className="widget-nav">
        <GenericErrorBoundary>
          <p style={{ display: 'flex' }}>
            {!dashboardOnlyMode && (
              <PermissionDecorator item="dashboard_move_widget">
                <WidgetDragHandle />
              </PermissionDecorator>
            )}
            <PermissionDecorator item="dashboard_rename_widget">
              <NameHandlerComponent
                widgetId={dashboardElementId}
                dashboardElementId={widget_id}
                dashboardId={gridId}
                dashboardType={gridType}
              />
            </PermissionDecorator>
          </p>

          <div className="nav-icon">
            <WidgetToolbar
              dashboardElementId={dashboardElementId}
              dashboardId={gridId}
              platformActions={platformActions}
            >
              {buttons.zoom && (
                <>
                  <Tooltip title="Zoom avant">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const button = document.querySelector(
                          `#widget-${widget_id} a[data-title="Zoom in"]`
                        );
                        if (button instanceof HTMLAnchorElement) {
                          button.click();
                        }
                      }}
                    >
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Zoom arrière">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const button = document.querySelector(
                          `#widget-${widget_id} a[data-title="Zoom out"]`
                        );
                        if (button instanceof HTMLAnchorElement) {
                          button.click();
                        }
                      }}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </WidgetToolbar>
          </div>
        </GenericErrorBoundary>
      </div>
    </PermissionDecorator>
  );
}

export default forwardRef(DisplayerNavComponent);
